import React, { ChangeEvent, useState } from 'react'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import TextField, { TextFieldProps } from 'components_new/atoms/TextField'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

interface SubmitTextFieldProps extends Omit<TextFieldProps, 'autoFocus'> {
  disabledSubmit?: boolean
  onSubmit: (value: string) => void
}

const SubmitTextField = React.forwardRef(
  (props: SubmitTextFieldProps, ref: any) => {
    const {
      disabledSubmit,
      helperText,
      onChange,
      onSubmit,
      sx,
      value,
      ...rest
    } = props

    const { t } = useTranslation()

    const [dialog, setDialog] = useState<{ open: boolean; value: string }>({
      open: false,
      value: ''
    })

    const handleClose = () => {
      setDialog((prevState) => ({
        ...prevState,
        open: false
      }))
    }

    const handleOpen = () => {
      setDialog({
        value: value,
        open: true
      })
    }

    const handleChange = (newValue: string) => {
      setDialog((prevState) => ({
        ...prevState,
        value: newValue
      }))
    }

    const handleSubmit = () => {
      onSubmit(dialog.value)
      handleClose()
    }

    return (
      <>
        <Box
          onClick={handleOpen}
          ref={ref}
          sx={{
            cursor: 'pointer',
            width: '100%',
            ['&:hover']: {
              ['& .MuiInputBase-root']: {
                bgcolor: 'action.focus'
              }
            }
          }}
        >
          <TextField
            autoFocus={false}
            sx={{
              ...sx,
              pointerEvents: 'none'
            }}
            value={value}
            {...rest}
          />
        </Box>

        <SimpleDialog
          actions={
            <>
              <Button onClick={handleClose} variant="text">
                {t('global', 'cancel')}
              </Button>
              <Button disabled={disabledSubmit} onClick={handleSubmit}>
                {t('global', 'save')}
              </Button>
            </>
          }
          onClose={handleClose}
          open={dialog.open}
        >
          <TextField
            autoFocus={true}
            helperText={helperText}
            onClick={handleOpen}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              if (onChange) {
                onChange(event)
              }
              handleChange(event.target.value)
            }}
            value={dialog.value}
            ref={ref}
            {...rest}
          />
        </SimpleDialog>
      </>
    )
  }
)

SubmitTextField.displayName = 'SubmitTextField'

export default SubmitTextField
